import JobStatusStepper from '@/components/jobs/JobStatusStepper';
import JobUploadCard from '@/components/jobs/JobUploadCard';
import { needsUpload } from '@/lib/dashboardActions';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { getJob } from '@/lib/dataService';
import { getActiveRegionConfig, getRegionConfig } from '@/lib/regionConfig';
import { getStatusBadge, getStatusLabel, isReviewStatus } from '@/lib/statusConfig';
import { jobsApi, paymentsApi } from '@/services/api';
import { WIZARD_SESSION_KEY, buildUploadSession, canResumeUpload } from '@/lib/uploadSession';
import { AlertCircle, ArrowLeft, Building2, CheckCircle, CreditCard, Download, Eye, FileText, Loader2, Mail } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewMode, setReviewMode] = useState('approve');
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [taxStatus, setTaxStatus] = useState(null);       // service status of the Tax Computation, when ordered
  const [taxHash, setTaxHash] = useState('');             // hash of the version the client downloaded
  const [taxBusy, setTaxBusy] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadJob = useCallback(async () => {
    try {
      const data = await getJob(jobId);
      setJob(data);
      setError(null);
      return data;
    } catch (err) {
      console.error('Failed to load job:', err);
      setError('Job not found or you do not have access.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  const loadTaxStatus = useCallback(async (uuid) => {
    try {
      const { services } = await jobsApi.listServices(uuid);
      setTaxStatus((services || []).find((s) => s.service_key === 'ixbrl_tc')?.service_status || null);
    } catch (err) {
      setTaxStatus(null); // the Tax card simply does not show; the rest of the job page is unaffected
    }
  }, []);

  useEffect(() => {
    if (job?.id) loadTaxStatus(job.id);
  }, [job?.id, loadTaxStatus]);

  const handleTaxDownload = async () => {
    setTaxBusy(true);
    try {
      const { blob, filename, packageHash } = await jobsApi.downloadServicePackage(job.id, 'ixbrl_tc');
      setTaxHash(packageHash);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Could not download the tax computation.');
    } finally {
      setTaxBusy(false);
    }
  };

  const handleTaxApprove = async () => {
    setTaxBusy(true);
    try {
      await jobsApi.approveService(job.id, 'ixbrl_tc', taxHash);
      toast.success('Tax computation approved.');
      setTaxHash('');
      await loadTaxStatus(job.id);
    } catch (err) {
      toast.error(err.message || 'Could not approve the tax computation.');
    } finally {
      setTaxBusy(false);
    }
  };

  // Returning from Stripe Checkout: confirm the payment server-side (which also
  // unlocks the job), then drop the session id from the URL so a refresh or a
  // shared link cannot re-run it.
  const returnedSessionId = searchParams.get('stripe_session_id') || searchParams.get('pay_ref');
  useEffect(() => {
    if (!returnedSessionId) return undefined;
    let cancelled = false;
    setPaymentProcessing(true);
    paymentsApi.verifyReturn(searchParams)
      .then(async (result) => {
        if (cancelled) return;
        if (result.paid) {
          toast.success('Payment received. Thank you - there is no need to pay again.');
          const paidJob = await loadJob();
          if (cancelled) return;
          if (canResumeUpload(paidJob)) {
            // Paid but no file yet: take the client straight to the upload step for this job.
            sessionStorage.setItem(WIZARD_SESSION_KEY, JSON.stringify(buildUploadSession(paidJob, returnedSessionId)));
            // Stop the finally block below from resetting the search params on this page afterwards:
            // that replace-navigation would override the move to the upload step.
            cancelled = true;
            navigate('/wizard/upload', { replace: true });
            return;
          }
        } else if (result.processing) {
          toast.info('Your payment is being processed. Please do not pay again - this page will update shortly.');
        } else {
          toast.error('Payment was not completed. You have not been charged.');
        }
        await loadJob();
      })
      .catch(() => {
        if (!cancelled) toast.error('We could not confirm your payment yet. If you were charged, please do not pay again - contact support.');
      })
      .finally(() => {
        if (cancelled) return;
        setPaymentProcessing(false);
        setSearchParams({}, { replace: true });
      });
    return () => { cancelled = true; };
  }, [returnedSessionId, loadJob, setSearchParams, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold mb-2">Job Not Found</h2>
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button variant="outline" onClick={() => navigate('/jobs')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Button>
      </div>
    );
  }

  const regionConfig = getRegionConfig(job.regionCode || getActiveRegionConfig().code);
  const serviceNames = {
    financialStatements: 'iXBRL Financial Statements',
    taxComputation: 'iXBRL Tax Computation',
    ct600: 'CT600 Filing',
    companiesHouse: 'Companies House Filing',
    ...(regionConfig.serviceCatalog
      ? Object.fromEntries(Object.entries(regionConfig.serviceCatalog).map(([key, value]) => [key, value.title]))
      : {}),
  };

  const timelineEvents = (job.statusHistory || []).map((entry) => ({
    status: entry.status,
    timestamp: entry.timestamp,
    label: getStatusLabel(entry.status),
  }));

  const ixbrlReady = job.tagfshStatus === 'packaged';

  const handleDownload = async () => {
    if (!ixbrlReady) {
      toast.info('Download will be available when conversion is complete.');
      return;
    }
    try {
      const { blob, filename } = await jobsApi.downloadIxbrlPackage(job.id || jobId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || 'Could not download the iXBRL package.');
    }
  };

  const handlePayNow = async () => {
    if (payLoading || paymentProcessing) return;
    setPayLoading(true);
    try {
      const express = job.deliveryTime === 'express';
      const companyIds = job.companyId ? [job.companyId] : [];
      // Jobs created through the current wizard use entity-based pricing
      // (Step3Review.js builds the same {company_id: account_type} shape) --
      // job.services is only ever populated for the legacy service-based
      // flow, so sending it here left entity-priced jobs matching neither
      // branch on the backend and failing with "Please provide either
      // entity_prices or legacy services/company_count".
      const entity_prices = {};
      for (const id of companyIds) {
        entity_prices[id] = job.accountType || 'standard';
      }
      const { url } = await paymentsApi.startCheckout({
        entity_prices,
        express,
        // Lets the server link the payment to this job and refuse a second payment.
        job_ids: job.id ? [job.id] : [],
        return_url: `${window.location.origin}${window.location.pathname}`,
      });
      window.location.href = url;
    } catch (err) {
      if (err.status === 409) {
        // Already paid (or paid and still settling): refresh instead of letting them pay again.
        toast.info(err.responseDetail || 'This order has already been paid.');
        await loadJob();
      } else {
        toast.error(err.message || 'Could not start payment. Please try again.');
      }
      setPayLoading(false);
    }
  };

  const openReviewDialog = (mode) => {
    setReviewMode(mode);
    setReviewComment('');
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    if (reviewMode === 'changes' && !reviewComment.trim()) {
      toast.error('Please describe the changes you need.');
      return;
    }

    setSubmittingReview(true);

    try {
      if (reviewMode === 'approve') {
        await jobsApi.approveReview(job.jobId);
        toast.success('Review approved. Your job status has been updated.');
      } else {
        await jobsApi.requestChanges(job.jobId, reviewComment.trim());
        toast.success('Change request sent to the expert team.');
      }

      setReviewDialogOpen(false);
      await loadJob();
    } catch (err) {
      toast.error(err.message || 'Could not update the review status.');
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <div data-testid="job-detail-page">
      <div className="page-header flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/jobs')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="page-title">Job {job.jobId}</h1>
            <p className="page-subtitle">{job.companyName}</p>
            {job.tagfshStatus && (
              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
                {job.tagfshStatus === 'packaged' ? 'iXBRL packaged' : 'Sent for tagging'}
              </span>
            )}
            {job.tagfshV2Status && (
              <span className="inline-flex items-center mt-1 ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
                FDR: {job.tagfshV2Status.replaceAll('_', ' ')}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(job.status === 'completed' || job.status === 'filed') && (
            <Button onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Download iXBRL
            </Button>
          )}
          {(job.status === 'payment_pending' || job.status === 'awaiting_payment') && job.paymentStatus !== 'paid' && (
            <Button onClick={handlePayNow} disabled={payLoading || paymentProcessing} data-testid="job-pay-now-btn">
              {payLoading || paymentProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
              {paymentProcessing ? 'Confirming payment…' : payLoading ? 'Redirecting…' : 'Pay Now'}
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Progress stepper */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">PROGRESS</h2>
            <JobStatusStepper status={job.status} />
          </div>

          {/* Paid or on invoice, but no file yet: upload it right here */}
          {needsUpload(job) && <JobUploadCard job={job} onUploaded={loadJob} />}

          {/* Review required */}
          {isReviewStatus(job.status) && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Eye className="w-5 h-5 text-green-700" />
                </div>
                <div>
                  <p className="font-medium text-green-900 mb-1">Your iXBRL is ready for review</p>
                  <p className="text-sm text-green-700 mb-3">
                    Review the tagged work and either approve it for the next stage or send precise feedback back to the provider team.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => openReviewDialog('approve')}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openReviewDialog('changes')}>
                      Request Changes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tax computation: download the version, then approve exactly that version */}
          {taxStatus === 'client_review' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-5" data-testid="tax-review-card">
              <p className="font-medium text-green-900 mb-1">Your tax computation is ready for review</p>
              <p className="text-sm text-green-700 mb-3">
                Download it, check it, then approve. Your approval is recorded against the version you downloaded.
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleTaxDownload} disabled={taxBusy} data-testid="tax-download-button">
                  <Download className="w-3 h-3 mr-1" /> Download
                </Button>
                <Button size="sm" onClick={handleTaxApprove} disabled={taxBusy || !taxHash} data-testid="tax-approve-button">
                  <CheckCircle className="w-3 h-3 mr-1" /> Approve
                </Button>
              </div>
              {!taxHash && <p className="text-xs text-green-700 mt-2">Download the file first to enable approval.</p>}
            </div>
          )}
          {['approved', 'ready_to_file', 'submitted', 'filed', 'completed'].includes(taxStatus) && (
            <div className="bg-card border border-border rounded-lg p-4 text-sm" data-testid="tax-approved-note">
              Tax computation approved.
            </div>
          )}

          {/* On Hold notice */}
          {job.status === 'on_hold' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="font-medium text-amber-900 mb-1">This job is on hold</p>
                  {job.onHoldReason && <p className="text-sm text-amber-700">{job.onHoldReason}</p>}
                  {!job.onHoldReason && <p className="text-sm text-amber-700">Our team will be in touch. Please contact support if you need assistance.</p>}
                </div>
              </div>
            </div>
          )}

          {/* Filing status */}
          {(job.chFilingStatus || job.hmrcFilingStatus) && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-sm font-medium text-muted-foreground mb-4">FILING STATUS</h2>
              <div className="space-y-2">
                {job.chFilingStatus && (
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm">{regionConfig.filingStatusLabels?.ch || 'Companies House'}</span>
                    <span className="text-xs font-medium capitalize">{job.chFilingStatus.replace(/_/g, ' ')}</span>
                  </div>
                )}
                {job.hmrcFilingStatus && (
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <span className="text-sm">{regionConfig.filingStatusLabels?.hmrc || 'HMRC'}</span>
                    <span className="text-xs font-medium capitalize">{job.hmrcFilingStatus.replace(/_/g, ' ')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary card */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">JOB SUMMARY</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                {getStatusBadge(job.status)}
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Service Type</p>
                <p className="text-sm font-medium">{job.serviceType === 'diy' ? 'DIY with AI' : 'Expert Help'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Delivery</p>
                <p className="text-sm font-medium capitalize">{job.deliveryTime || 'Standard'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Payment</p>
                <p className="text-sm font-medium">{job.paymentType === 'pay_now' ? 'Paid' : 'Invoice'}</p>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">SERVICES</h2>
            <div className="space-y-2">
              {(job.services || []).map((svc) => (
                <div key={svc} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm">{serviceNames[svc] || svc}</span>
                </div>
              ))}
            </div>
          </div>

          {/* File */}
          {job.fileName && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-sm font-medium text-muted-foreground mb-4">FILES</h2>
              <div className="flex items-center gap-3 p-3 border border-border rounded-lg">
                <FileText className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{job.fileName}</p>
                  <p className="text-xs text-muted-foreground">Uploaded {job.date}</p>
                </div>
                {(job.status === 'completed' || job.status === 'filed') && (
                  <Button variant="outline" size="sm" onClick={handleDownload}>
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="space-y-6">
          {/* Company card */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">COMPANY</h2>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-sm">{job.companyName}</p>
                {job.accountType && (
                  <p className="text-xs text-muted-foreground capitalize">{job.accountType} accounts</p>
                )}
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Financial Year</span>
                <span className="font-medium">{job.financialYear || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Est. Delivery</span>
                <span className="font-medium">{job.estimatedDelivery || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Created</span>
                <span className="font-medium">{job.date}</span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-4">TIMELINE</h2>
            {timelineEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No events recorded yet.</p>
            ) : (
              <div className="space-y-0">
                {timelineEvents.map((event, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                      {idx < timelineEvents.length - 1 && (
                        <div className="w-px flex-1 bg-border min-h-[24px]" />
                      )}
                    </div>
                    <div className="pb-4">
                      <p className="text-sm font-medium">{event.label}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(event.timestamp).toLocaleString('en-GB', {
                          day: 'numeric', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Support */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-sm font-medium text-muted-foreground mb-3">NEED HELP?</h2>
            <Button variant="outline" className="w-full" asChild>
              <a href={`mailto:${regionConfig.careEmail}`}>
                <Mail className="w-4 h-4 mr-2" />
                Contact Support
              </a>
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{reviewMode === 'approve' ? 'Approve iXBRL Review' : 'Request Changes'}</DialogTitle>
          </DialogHeader>

          {reviewMode === 'approve' ? (
            <p className="text-sm text-muted-foreground">
              Approving will move this job to the next stage in the shared provider workflow.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Tell the provider team exactly what should be updated before the job returns to your review queue.
              </p>
              <Textarea
                value={reviewComment}
                onChange={(event) => setReviewComment(event.target.value)}
                placeholder="Describe the corrections or additions you need..."
                rows={5}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)} disabled={submittingReview}>
              Cancel
            </Button>
            <Button onClick={handleReviewSubmit} disabled={submittingReview}>
              {submittingReview ? 'Saving...' : reviewMode === 'approve' ? 'Approve Job' : 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDetail;
